import dayjs from 'dayjs';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FiCalendar,
  FiCheck,
  FiChevronDown,
  FiSearch,
  FiX,
} from 'react-icons/fi';
import {FilterIcon} from '@ui/atoms/icons/filter-icon';
import {Checkbox} from '@ui/atoms/checkbox';
import {Calendar} from '@ui/molecules/calendar';
import {
  emptyMyGoalsFilters,
  MyGoalsFilters,
  MyGoalsPeriod,
  MyGoalsSort,
} from '../my-objective/my-goals-v2.types';
import {
  activeFilterCount,
  standardQuarter,
} from '../my-objective/my-goals-v2.utils';
import {
  AppliedFilters,
  CheckOption,
  ControlButton,
  ControlWrap,
  DateCalendarMenu,
  DateInputButton,
  DatePopover,
  DatePresetGrid,
  DateSelectButton,
  DateSelectField,
  DateSelectMenu,
  DateSelectOption,
  FieldGrid,
  FilterChip,
  FilterCount,
  FilterPopover,
  FilterSection,
  PopoverBody,
  PopoverFooter,
  PopoverHeader,
  SmallPrimaryButton,
  SortMenu,
  SortOption,
  TextButton,
  ToolbarDivider,
  ToolbarGroup,
  ToolbarMeta,
  ToolbarPanel,
} from '../my-objective/my-goals-v2.styles';
import {GroupByControl} from './group-by-control';
import {TeamGoalsGroupBy} from './team-goals.types';
import {
  EmptyFilterOptions,
  FilterOptionSearch,
  TeamToolbar,
  ToolbarSearch,
} from './styles';

const sortLabels: Record<MyGoalsSort, string> = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  highest_progress: 'Highest progress',
  due_date: 'Due date (oldest)',
};

const filterGroups: Array<{
  title: string;
  key: keyof MyGoalsFilters;
  options: Array<{value: string; label: string}>;
}> = [
  {
    title: 'Type',
    key: 'types',
    options: [
      {value: 'objective', label: 'Objective'},
      {value: 'kpi', label: 'KPI'},
    ],
  },
  {
    title: 'Scope',
    key: 'scopes',
    options: [
      {value: 'personal', label: 'Personal'},
      {value: 'team', label: 'Team'},
      {value: 'company', label: 'Company'},
    ],
  },
  {
    title: 'Status',
    key: 'performance',
    options: [
      {value: 'on_track', label: 'On track'},
      {value: 'at_risk', label: 'At risk'},
      {value: 'behind', label: 'Behind'},
      {value: 'upcoming', label: 'Upcoming'},
      {value: 'no_status', label: 'No status'},
    ],
  },
];

const filterLabels: Record<string, string> = {
  objective: 'Objectives',
  kpi: 'KPIs',
  personal: 'Personal',
  team: 'Team',
  company: 'Company-wide',
  on_track: 'On track',
  at_risk: 'At risk',
  behind: 'Behind',
  upcoming: 'Upcoming',
  no_status: 'No status',
};

export interface TeamMemberOption {
  id: string;
  name: string;
}

export interface SelectionFilterGroup {
  key: string;
  title: string;
  options: TeamMemberOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

interface TeamGoalsToolbarProps<T extends string> {
  goals: any[];
  filters: MyGoalsFilters;
  sort: MyGoalsSort;
  groupBy: T;
  groupByOptions?: Array<{value: T; label: string}>;
  period: MyGoalsPeriod;
  defaultPeriod: MyGoalsPeriod;
  memberOptions: TeamMemberOption[];
  selectedPeople: string[];
  peopleLabel?: string;
  selectionFilterGroups?: SelectionFilterGroup[];
  pageLabel?: string;
  searchValue?: string;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  filterSearchable?: boolean;
  includeKpiFilter?: boolean;
  showSort?: boolean;
  showGroupBy?: boolean;
  toolbarAction?: ReactNode;
  reviewSuggestedOnly?: boolean;
  resultCount: number;
  onFiltersChange: (filters: MyGoalsFilters) => void;
  onSortChange: (sort: MyGoalsSort) => void;
  onGroupByChange: (groupBy: T) => void;
  onPeriodChange: (period: MyGoalsPeriod) => void;
  onSelectedPeopleChange: (people: string[]) => void;
  onClearStatusReview?: () => void;
}

export const TeamGoalsToolbar = <T extends string = TeamGoalsGroupBy>({
  goals,
  filters,
  sort,
  groupBy,
  groupByOptions = [
    {value: 'person' as T, label: 'Person'},
    {value: 'status' as T, label: 'Status'},
  ],
  period,
  defaultPeriod,
  memberOptions,
  selectedPeople,
  peopleLabel = 'People',
  selectionFilterGroups = [],
  pageLabel = 'Team Goals',
  searchValue,
  searchPlaceholder = 'Search goals',
  onSearchChange,
  filterSearchable = false,
  includeKpiFilter = true,
  showSort = true,
  showGroupBy = true,
  toolbarAction,
  reviewSuggestedOnly = false,
  resultCount,
  onFiltersChange,
  onSortChange,
  onGroupByChange,
  onPeriodChange,
  onSelectedPeopleChange,
  onClearStatusReview,
}: TeamGoalsToolbarProps<T>) => {
  const [openControl, setOpenControl] = useState<
    'filter' | 'sort' | 'group' | 'date' | null
  >(null);
  const [draftYear, setDraftYear] = useState(period.year);
  const [draftQuarter, setDraftQuarter] = useState<number | 'custom'>(
    period.quarter,
  );
  const [customStart, setCustomStart] = useState(
    dayjs(period.starts).format('YYYY-MM-DD'),
  );
  const [customEnd, setCustomEnd] = useState(
    dayjs(period.ends).format('YYYY-MM-DD'),
  );
  const [openDateSelect, setOpenDateSelect] = useState<
    'year' | 'quarter' | 'start' | 'end' | null
  >(null);
  const [filterSearch, setFilterSearch] = useState('');
  const toolbarRef = useRef<HTMLElement>(null);
  const closeControls = useCallback(() => {
    setOpenControl(null);
    setOpenDateSelect(null);
  }, []);

  useEffect(() => {
    if (openControl === null) return;
    const close = (event: MouseEvent) => {
      if (
        toolbarRef.current &&
        !toolbarRef.current.contains(event.target as Node)
      ) {
        closeControls();
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [closeControls, openControl]);

  useEffect(() => {
    if (openControl !== 'filter') setFilterSearch('');
  }, [openControl]);

  const years = useMemo(() => {
    const values = new Set<number>([
      dayjs().year() - 2,
      dayjs().year() - 1,
      dayjs().year(),
      dayjs().year() + 1,
    ]);
    goals.forEach((goal) => {
      if (goal?.startDate) values.add(dayjs(goal.startDate).year());
      if (goal?.endDate) values.add(dayjs(goal.endDate).year());
    });
    return Array.from(values).sort((a, b) => b - a);
  }, [goals]);

  const filterCount =
    activeFilterCount(filters) +
    selectedPeople.length +
    selectionFilterGroups.reduce(
      (count, group) => count + group.selected.length,
      0,
    );
  const toggleFilter = (key: keyof MyGoalsFilters, value: string) => {
    const current = filters[key] as string[];
    onFiltersChange({
      ...filters,
      [key]: current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value],
    } as MyGoalsFilters);
  };
  const togglePerson = (personId: string) => {
    onSelectedPeopleChange(
      selectedPeople.includes(personId)
        ? selectedPeople.filter((id) => id !== personId)
        : [...selectedPeople, personId],
    );
  };
  const toggleSelection = (group: SelectionFilterGroup, optionId: string) => {
    group.onChange(
      group.selected.includes(optionId)
        ? group.selected.filter((id) => id !== optionId)
        : [...group.selected, optionId],
    );
  };
  const toggleControl = (control: 'filter' | 'sort' | 'date') => {
    setOpenDateSelect(null);
    if (control === 'date') {
      setDraftYear(period.year);
      setDraftQuarter(period.quarter);
      setCustomStart(dayjs(period.starts).format('YYYY-MM-DD'));
      setCustomEnd(dayjs(period.ends).format('YYYY-MM-DD'));
    }
    setOpenControl((current) => (current === control ? null : control));
  };
  const resetFilters = () => {
    onFiltersChange(emptyMyGoalsFilters);
    onSelectedPeopleChange([]);
    selectionFilterGroups.forEach((group) => group.onChange([]));
    if (reviewSuggestedOnly) onClearStatusReview?.();
  };
  const appliedFilters: Array<{
    key: string;
    value: string;
    label: string;
    source: 'base' | 'people' | 'selection';
  }> = [
    ...Object.entries(filters).flatMap(([key, values]) =>
      (values as string[]).map((value) => ({
        key,
        value,
        label: filterLabels[value],
        source: 'base' as const,
      })),
    ),
    ...selectedPeople.map((value) => ({
      key: 'people',
      value,
      label:
        memberOptions.find((option) => option.id === value)?.name ||
        'Team member',
      source: 'people' as const,
    })),
    ...selectionFilterGroups.flatMap((group) =>
      group.selected.map((value) => ({
        key: group.key,
        value,
        label:
          group.options.find((option) => option.id === value)?.name ||
          group.title,
        source: 'selection' as const,
      })),
    ),
  ];
  const periodMeta =
    period.quarter === 'custom'
      ? `${dayjs(period.starts).format('D MMM YYYY')} – ${dayjs(
          period.ends,
        ).format('D MMM YYYY')}`
      : `${period.year} · Q${period.quarter}`;
  const dateSelectionIsDefault =
    draftYear === defaultPeriod.year &&
    draftQuarter === defaultPeriod.quarter &&
    (draftQuarter !== 'custom' ||
      (customStart === dayjs(defaultPeriod.starts).format('YYYY-MM-DD') &&
        customEnd === dayjs(defaultPeriod.ends).format('YYYY-MM-DD')));
  const optionQuery = filterSearch.trim().toLowerCase();
  const optionMatches = (value: string) =>
    !optionQuery || value.toLowerCase().includes(optionQuery);
  const availableFilterGroups = includeKpiFilter
    ? filterGroups
    : filterGroups.map((group) =>
        group.key === 'types'
          ? {
              ...group,
              options: group.options.filter((option) => option.value !== 'kpi'),
            }
          : group,
      );
  const visibleFilterGroups = availableFilterGroups
    .map((group) => ({
      ...group,
      options: optionMatches(group.title)
        ? group.options
        : group.options.filter((option) => optionMatches(option.label)),
    }))
    .filter((group) => group.options.length > 0);
  const visibleSelectionGroups = selectionFilterGroups
    .map((group) => ({
      ...group,
      options: optionMatches(group.title)
        ? group.options
        : group.options.filter((option) => optionMatches(option.name)),
    }))
    .filter((group) => group.options.length > 0);
  const visibleMembers = optionMatches(peopleLabel)
    ? memberOptions
    : memberOptions.filter((member) => optionMatches(member.name));
  const noFilterOptions =
    visibleFilterGroups.length === 0 &&
    visibleSelectionGroups.length === 0 &&
    visibleMembers.length === 0;

  return (
    <ToolbarPanel ref={toolbarRef}>
      <TeamToolbar $withSearch={Boolean(onSearchChange)}>
        <ToolbarGroup>
          {onSearchChange && (
            <ToolbarSearch>
              <FiSearch size={16} aria-hidden="true" />
              <input
                type="search"
                value={searchValue || ''}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                onChange={(event) => onSearchChange(event.target.value)}
              />
              {searchValue && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => onSearchChange('')}>
                  <FiX size={14} />
                </button>
              )}
            </ToolbarSearch>
          )}
          <ControlWrap>
            <ControlButton
              type="button"
              $active={openControl === 'filter' || filterCount > 0}
              aria-expanded={openControl === 'filter'}
              onClick={() => toggleControl('filter')}>
              <FilterIcon stroke="currentColor" /> Filter
              {filterCount > 0 && <FilterCount>{filterCount}</FilterCount>}
              <FiChevronDown />
            </ControlButton>
            {openControl === 'filter' && (
              <FilterPopover>
                <PopoverBody>
                  <PopoverHeader>
                    <strong>Filter {pageLabel}</strong>
                    <TextButton
                      type="button"
                      onClick={() => setOpenControl(null)}>
                      Cancel
                    </TextButton>
                  </PopoverHeader>
                  {filterSearchable && (
                    <FilterOptionSearch>
                      <FiSearch size={15} aria-hidden="true" />
                      <input
                        type="search"
                        value={filterSearch}
                        placeholder="Search filter options"
                        aria-label="Search filter options"
                        onChange={(event) =>
                          setFilterSearch(event.target.value)
                        }
                      />
                      {filterSearch && (
                        <button
                          type="button"
                          aria-label="Clear filter search"
                          onClick={() => setFilterSearch('')}>
                          <FiX size={14} />
                        </button>
                      )}
                    </FilterOptionSearch>
                  )}
                  {visibleFilterGroups.map((group) => (
                    <FilterSection key={group.key}>
                      <h3>{group.title}</h3>
                      {group.options.map((option) => (
                        <CheckOption key={option.value}>
                          <Checkbox
                            boxSize="small"
                            checked={(filters[group.key] as string[]).includes(
                              option.value,
                            )}
                            aria-label={option.label}
                            onChange={() =>
                              toggleFilter(group.key, option.value)
                            }
                          />
                          {option.label}
                        </CheckOption>
                      ))}
                    </FilterSection>
                  ))}
                  {visibleSelectionGroups.map((group) => (
                    <FilterSection key={group.key}>
                      <h3>{group.title}</h3>
                      {group.options.map((option) => (
                        <CheckOption key={option.id}>
                          <Checkbox
                            boxSize="small"
                            checked={group.selected.includes(option.id)}
                            aria-label={option.name}
                            onChange={() => toggleSelection(group, option.id)}
                          />
                          {option.name}
                        </CheckOption>
                      ))}
                    </FilterSection>
                  ))}
                  {visibleMembers.length > 0 && (
                    <FilterSection>
                      <h3>{peopleLabel}</h3>
                      {visibleMembers.map((member) => (
                        <CheckOption key={member.id}>
                          <Checkbox
                            boxSize="small"
                            checked={selectedPeople.includes(member.id)}
                            aria-label={member.name}
                            onChange={() => togglePerson(member.id)}
                          />
                          {member.name}
                        </CheckOption>
                      ))}
                    </FilterSection>
                  )}
                  {noFilterOptions && (
                    <EmptyFilterOptions>
                      No filter options found.
                    </EmptyFilterOptions>
                  )}
                </PopoverBody>
                <PopoverFooter>
                  <TextButton
                    type="button"
                    $active={filterCount > 0 || reviewSuggestedOnly}
                    disabled={filterCount === 0 && !reviewSuggestedOnly}
                    onClick={resetFilters}>
                    Clear all
                  </TextButton>
                </PopoverFooter>
              </FilterPopover>
            )}
          </ControlWrap>
        </ToolbarGroup>

        <ToolbarGroup>
          {showSort && (
            <ControlWrap>
              <ControlButton
                type="button"
                aria-expanded={openControl === 'sort'}
                onClick={() => toggleControl('sort')}>
                <span className="toolbar-control-prefix">Sort by</span>{' '}
                <strong>{sortLabels[sort]}</strong> <FiChevronDown />
              </ControlButton>
              {openControl === 'sort' && (
                <SortMenu $align="right">
                  {(Object.keys(sortLabels) as MyGoalsSort[]).map((value) => (
                    <SortOption
                      key={value}
                      type="button"
                      $active={sort === value}
                      onClick={() => {
                        onSortChange(value);
                        setOpenControl(null);
                      }}>
                      {sortLabels[value]} {sort === value && <FiCheck />}
                    </SortOption>
                  ))}
                </SortMenu>
              )}
            </ControlWrap>
          )}

          {showGroupBy && (
            <GroupByControl
              value={groupBy}
              options={groupByOptions}
              ariaLabel={`Group ${pageLabel} by`}
              open={openControl === 'group'}
              onOpenChange={(open) => {
                setOpenDateSelect(null);
                setOpenControl(open ? 'group' : null);
              }}
              onChange={onGroupByChange}
            />
          )}

          {toolbarAction}

          {(showSort || showGroupBy || toolbarAction) && (
            <ToolbarDivider aria-hidden="true" />
          )}

          <ControlWrap>
            <ControlButton
              type="button"
              $active={openControl === 'date'}
              aria-expanded={openControl === 'date'}
              onClick={() => toggleControl('date')}>
              <FiCalendar />{' '}
              {period.quarter === 'custom' ? 'Custom' : period.label}{' '}
              <FiChevronDown />
            </ControlButton>
            {openControl === 'date' && (
              <DatePopover $align="right">
                <PopoverBody>
                  <PopoverHeader>
                    <strong>Select date</strong>
                    <TextButton type="button" onClick={closeControls}>
                      Cancel
                    </TextButton>
                  </PopoverHeader>
                  <DatePresetGrid>
                    <DateSelectField>
                      <span>Year</span>
                      <DateSelectButton
                        type="button"
                        $open={openDateSelect === 'year'}
                        aria-label="Select year"
                        aria-haspopup="listbox"
                        aria-expanded={openDateSelect === 'year'}
                        onClick={() =>
                          setOpenDateSelect((current) =>
                            current === 'year' ? null : 'year',
                          )
                        }>
                        <span>{draftYear}</span>
                        <FiChevronDown size={16} />
                      </DateSelectButton>
                      {openDateSelect === 'year' && (
                        <DateSelectMenu
                          role="listbox"
                          aria-label="Year options">
                          {years.map((year) => (
                            <DateSelectOption
                              key={year}
                              type="button"
                              role="option"
                              $active={draftYear === year}
                              aria-selected={draftYear === year}
                              onClick={() => {
                                setDraftYear(year);
                                setOpenDateSelect(null);
                              }}>
                              <span>{year}</span>
                              {draftYear === year && <FiCheck size={14} />}
                            </DateSelectOption>
                          ))}
                        </DateSelectMenu>
                      )}
                    </DateSelectField>
                    <DateSelectField>
                      <span>Quarter</span>
                      <DateSelectButton
                        type="button"
                        $open={openDateSelect === 'quarter'}
                        aria-label="Select quarter"
                        aria-haspopup="listbox"
                        aria-expanded={openDateSelect === 'quarter'}
                        onClick={() =>
                          setOpenDateSelect((current) =>
                            current === 'quarter' ? null : 'quarter',
                          )
                        }>
                        <span>
                          {draftQuarter === 'custom'
                            ? 'Custom range'
                            : `Q${draftQuarter} ${draftYear}`}
                        </span>
                        <FiChevronDown size={16} />
                      </DateSelectButton>
                      {openDateSelect === 'quarter' && (
                        <DateSelectMenu
                          role="listbox"
                          aria-label="Quarter options">
                          {[1, 2, 3, 4].map((quarter) => (
                            <DateSelectOption
                              key={quarter}
                              type="button"
                              role="option"
                              $active={draftQuarter === quarter}
                              aria-selected={draftQuarter === quarter}
                              onClick={() => {
                                setDraftQuarter(quarter);
                                setOpenDateSelect(null);
                              }}>
                              <span>
                                Q{quarter} {draftYear}
                              </span>
                              {draftQuarter === quarter && (
                                <FiCheck size={14} />
                              )}
                            </DateSelectOption>
                          ))}
                          <DateSelectOption
                            type="button"
                            role="option"
                            $active={draftQuarter === 'custom'}
                            aria-selected={draftQuarter === 'custom'}
                            onClick={() => {
                              setDraftQuarter('custom');
                              setOpenDateSelect(null);
                            }}>
                            <span>Custom range</span>
                            {draftQuarter === 'custom' && <FiCheck size={14} />}
                          </DateSelectOption>
                        </DateSelectMenu>
                      )}
                    </DateSelectField>
                  </DatePresetGrid>
                  {draftQuarter === 'custom' && (
                    <FieldGrid style={{marginTop: 14}}>
                      <DateSelectField>
                        <span>Starts</span>
                        <DateInputButton
                          type="button"
                          $open={openDateSelect === 'start'}
                          aria-label="Select start date"
                          aria-haspopup="dialog"
                          aria-expanded={openDateSelect === 'start'}
                          onClick={() =>
                            setOpenDateSelect((current) =>
                              current === 'start' ? null : 'start',
                            )
                          }>
                          <span>
                            {customStart
                              ? dayjs(customStart).format('DD/MM/YYYY')
                              : 'DD/MM/YYYY'}
                          </span>
                          <FiCalendar size={16} />
                        </DateInputButton>
                        {openDateSelect === 'start' && (
                          <DateCalendarMenu>
                            <Calendar
                              value={customStart}
                              disabledAfter={customEnd || undefined}
                              onChange={(date) => {
                                setCustomStart(
                                  dayjs(date).format('YYYY-MM-DD'),
                                );
                                setOpenDateSelect(null);
                              }}
                            />
                          </DateCalendarMenu>
                        )}
                      </DateSelectField>
                      <DateSelectField>
                        <span>Ends</span>
                        <DateInputButton
                          type="button"
                          $open={openDateSelect === 'end'}
                          aria-label="Select end date"
                          aria-haspopup="dialog"
                          aria-expanded={openDateSelect === 'end'}
                          onClick={() =>
                            setOpenDateSelect((current) =>
                              current === 'end' ? null : 'end',
                            )
                          }>
                          <span>
                            {customEnd
                              ? dayjs(customEnd).format('DD/MM/YYYY')
                              : 'DD/MM/YYYY'}
                          </span>
                          <FiCalendar size={16} />
                        </DateInputButton>
                        {openDateSelect === 'end' && (
                          <DateCalendarMenu $align="right">
                            <Calendar
                              value={customEnd}
                              disabledBefore={customStart || undefined}
                              onChange={(date) => {
                                setCustomEnd(dayjs(date).format('YYYY-MM-DD'));
                                setOpenDateSelect(null);
                              }}
                            />
                          </DateCalendarMenu>
                        )}
                      </DateSelectField>
                    </FieldGrid>
                  )}
                </PopoverBody>
                <PopoverFooter>
                  <TextButton
                    type="button"
                    $active={!dateSelectionIsDefault}
                    disabled={dateSelectionIsDefault}
                    onClick={() => {
                      onPeriodChange(defaultPeriod);
                      closeControls();
                    }}>
                    Reset to default
                  </TextButton>
                  <SmallPrimaryButton
                    type="button"
                    disabled={
                      draftQuarter === 'custom' &&
                      (!customStart ||
                        !customEnd ||
                        dayjs(customEnd).isBefore(dayjs(customStart)))
                    }
                    onClick={() => {
                      onPeriodChange(
                        draftQuarter === 'custom'
                          ? {
                              starts: dayjs(customStart).format('MM/DD/YYYY'),
                              ends: dayjs(customEnd).format('MM/DD/YYYY'),
                              label: 'Custom',
                              year: dayjs(customStart).year(),
                              quarter: 'custom',
                            }
                          : standardQuarter(draftYear, draftQuarter),
                      );
                      closeControls();
                    }}>
                    Apply
                  </SmallPrimaryButton>
                </PopoverFooter>
              </DatePopover>
            )}
          </ControlWrap>
        </ToolbarGroup>
      </TeamToolbar>

      <ToolbarMeta>
        <span>
          Showing{' '}
          <strong>
            {resultCount} goal{resultCount === 1 ? '' : 's'}
          </strong>{' '}
          for <strong>{periodMeta}</strong>
        </span>
        {(appliedFilters.length > 0 || reviewSuggestedOnly) && (
          <AppliedFilters aria-label="Applied filters">
            {reviewSuggestedOnly && (
              <FilterChip
                type="button"
                aria-label="Remove status review filter"
                onClick={onClearStatusReview}>
                Status review <FiX size={14} />
              </FilterChip>
            )}
            {appliedFilters.map((filter) => (
              <FilterChip
                key={`${filter.key}-${filter.value}`}
                type="button"
                onClick={() => {
                  if (filter.source === 'people') {
                    togglePerson(filter.value);
                  } else if (filter.source === 'selection') {
                    const group = selectionFilterGroups.find(
                      (item) => item.key === filter.key,
                    );
                    if (group) toggleSelection(group, filter.value);
                  } else {
                    toggleFilter(
                      filter.key as keyof MyGoalsFilters,
                      filter.value,
                    );
                  }
                }}>
                {filter.label} <FiX />
              </FilterChip>
            ))}
            <TextButton type="button" $active onClick={resetFilters}>
              Clear all
            </TextButton>
          </AppliedFilters>
        )}
      </ToolbarMeta>
    </ToolbarPanel>
  );
};
